import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Brain, TrendingUp, MessageSquare } from 'lucide-react';
import { updateModuleProgress, completeEnhancedSurvey } from '@/api/module';
import DynamicSurveyQuestion from './DynamicSurveyQuestion';
import { useSurveyQuestions } from '@/hooks/useSurveyQuestions';
import { useSurveyResponse } from '@/hooks/useSurveyResponse';
import { fetchResponses } from '@/api/survey';
import PageHeader from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ResponsiveContainer } from './ResponsiveContainer';
import { cn } from '@/lib/utils';
import AdminJsonToggle from './AdminJsonToggle';
import type { UserModule } from '@/types/api';
import type { SurveyType } from '@/types/surveyTypes';

interface EnhancedSurveyConfig {
  enableAIAnalysis?: boolean;
  showProgressInsights?: boolean;
  adaptiveQuestions?: boolean;
  realTimeScoring?: boolean;
  branchingLogic?: boolean;
  maxRetries?: number;
}

interface ProgressInsight {
  category: string;
  score: number;
  trend: 'up' | 'down' | 'stable';
  insight: string;
}

interface EnhancedSurveyModuleProps {
  userModule: UserModule;
  startAt: number;
}

/**
 * EnhancedSurveyModule - An AI-enhanced survey experience with real-time insights
 * Features:
 * - Real-time AI analysis of responses
 * - Dynamic question adaptation based on answers
 * - Progress insights and scoring
 * - Branching logic support
 * - Enhanced UI with visual feedback
 */
const EnhancedSurveyModule: React.FC<EnhancedSurveyModuleProps> = ({
  userModule,
  startAt
}) => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(startAt);
  const [showInsights, setShowInsights] = useState(false);
  const [progressInsights, setProgressInsights] = useState<ProgressInsight[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Parse enhanced survey configuration
  const config: EnhancedSurveyConfig = userModule.module.data 
    ? JSON.parse(userModule.module.data) 
    : {};
  
  // Determine survey type from module
  const surveyId = userModule.module.surveyId || 0;
  const surveyType: SurveyType = surveyId === 1 ? 'pulse' : 'shape';
  const surveyName = surveyType === 'pulse' ? 'PULSE' : 'SHAPE';
  
  // Fetch questions from backend
  const { data: questions, isLoading } = useSurveyQuestions(surveyId);
  
  // Fetch existing responses
  const { data: responses, refetch: refetchResponses } = useQuery({
    queryKey: ['survey-responses', surveyId, userModule.id],
    queryFn: () => fetchResponses(surveyId, userModule.id),
    enabled: !!surveyId && !!userModule.id
  });
  
  // Survey response mutation
  const submitResponseMutation = useSurveyResponse();
  
  const totalQuestions = questions?.length || 0;
  const currentQuestion = questions?.[currentQuestionIndex];
  
  // Get current answer from responses
  const getCurrentAnswer = () => {
    if (!currentQuestion || !responses) return undefined;
    
    const response = responses.find((r: { questionId: number; response?: number; textResponse?: string }) => 
      r.questionId === currentQuestion.id
    );
    if (!response) return undefined;
    
    return response.textResponse || response.response;
  };
  
  // Progress update mutation
  const progressMutation = useMutation({
    mutationFn: (checkpoint: number) => 
      updateModuleProgress(userModule.id, checkpoint),
    onError: (error) => {
      console.error('Failed to update progress:', error);
    }
  });
  
  // Module completion mutation
  const completeMutation = useMutation({
    mutationFn: () => completeEnhancedSurvey(userModule.id),
    onSuccess: (nextModule) => {
      if (nextModule) {
        window.location.reload();
      } else {
        navigate('/updates');
      }
    },
    onError: (error) => {
      console.error('Failed to complete module:', error);
    }
  });
  
  // Update progress in backend when question changes
  useEffect(() => {
    if (currentQuestionIndex !== startAt) {
      progressMutation.mutate(currentQuestionIndex);
    }
  }, [currentQuestionIndex]);
  
  // Calculate progress insights when enabled
  useEffect(() => {
    if (config.showProgressInsights && responses && questions) {
      calculateProgressInsights();
    }
  }, [responses, questions, config.showProgressInsights]);
  
  const calculateProgressInsights = () => {
    if (!responses || !questions) return;
    
    // Group responses by category
    const categoryScores: Record<string, number[]> = {};
    
    responses.forEach((response: any) => {
      const question = questions.find(q => q.id === response.questionId);
      if (question && question.category && response.response) {
        if (!categoryScores[question.category]) {
          categoryScores[question.category] = [];
        }
        categoryScores[question.category].push(response.response);
      }
    });
    
    // Calculate insights
    const insights: ProgressInsight[] = Object.entries(categoryScores).map(([category, scores]) => {
      const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
      const trend = scores.length > 1 
        ? scores[scores.length - 1] > scores[0] ? 'up' 
        : scores[scores.length - 1] < scores[0] ? 'down' 
        : 'stable'
        : 'stable';
      
      return {
        category,
        score: Math.round(avgScore * 20), // Convert 1-5 to percentage
        trend,
        insight: generateInsight(category, avgScore, trend)
      };
    });
    
    setProgressInsights(insights);
  };
  
  const generateInsight = (category: string, score: number, trend: string): string => {
    // Simple insight generation - in real implementation, this would be AI-powered
    if (score >= 4) {
      return `Strong performance in ${category}. ${trend === 'up' ? 'Continuing to improve!' : 'Keep it up!'}`;
    } else if (score >= 3) {
      return `Moderate performance in ${category}. ${trend === 'up' ? 'Showing improvement.' : 'Room for growth.'}`;
    } else {
      return `${category} needs attention. ${trend === 'up' ? 'Starting to improve.' : 'Consider focusing here.'}`;
    }
  };
  
  const handleBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex]);
  
  const handleNext = useCallback(async () => {
    if (!totalQuestions) return;
    
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      
      // Trigger AI analysis if enabled
      if (config.enableAIAnalysis && (currentQuestionIndex + 1) % 5 === 0) {
        analyzeResponses();
      }
    } else {
      // Survey complete
      completeMutation.mutate();
    }
  }, [currentQuestionIndex, totalQuestions, completeMutation, config.enableAIAnalysis]);
  
  const analyzeResponses = async () => {
    setIsAnalyzing(true);
    // Simulate AI analysis - in real implementation, this would call an AI service
    setTimeout(() => {
      setAiAnalysis(`Based on your responses so far, you're showing strong self-awareness and growth mindset. Your answers suggest you value collaboration and continuous improvement.`);
      setIsAnalyzing(false);
    }, 2000);
  };
  
  const handleAnswer = useCallback(async (answer: number | string) => {
    if (!currentQuestion) return;
    
    submitResponseMutation.mutate({
      userModuleId: userModule.id,
      questionId: currentQuestion.id,
      response: answer
    }, {
      onSuccess: () => {
        refetchResponses();
      }
    });
  }, [currentQuestion, userModule.id, submitResponseMutation, refetchResponses]);
  
  if (isLoading || !currentQuestion) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-gray-600">Loading enhanced survey...</p>
        </div>
      </div>
    );
  }

  const progress = Math.round((currentQuestionIndex / totalQuestions) * 100);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <AdminJsonToggle 
        data={{
          module: userModule.module,
          config,
          currentQuestionIndex,
          totalQuestions,
          surveyType,
          responses: responses || [],
          progressInsights,
          aiAnalysis
        }}
        label="Enhanced Survey Module Data"
      />
      <ResponsiveContainer maxWidth="7xl" className="flex-1 py-0">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Main Survey Content */}
          <div className="lg:col-span-2">
            <DynamicSurveyQuestion
              questionNumber={currentQuestionIndex + 1}
              totalQuestions={totalQuestions}
              question={currentQuestion}
              onAnswer={handleAnswer}
              currentAnswer={getCurrentAnswer()}
              onBack={handleBack}
              onNext={handleNext}
              canGoBack={currentQuestionIndex > 0}
              canGoNext={true}
              surveyName={surveyName}
              module={userModule.module}
            />
            
            {/* AI Analysis Card */}
            {config.enableAIAnalysis && aiAnalysis && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <Brain className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-1">AI Insight</h3>
                    <p className="text-sm text-blue-800">{aiAnalysis}</p>
                  </div>
                </div>
              </div>
            )}
            
            {isAnalyzing && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
                  <p className="text-sm text-gray-600">Analyzing your responses...</p>
                </div>
              </div>
            )}
          </div>

          {/* Progress Insights Sidebar - Desktop Only */}
          {config.showProgressInsights && (
            <div className="hidden lg:block lg:col-span-1">
              <div className="sticky top-6 bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Progress Insights
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInsights(!showInsights)}
                  >
                    {showInsights ? 'Hide' : 'Show'}
                  </Button>
                </div>
                
                {showInsights && progressInsights.length > 0 && (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-4">
                      {progressInsights.map((insight, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-md">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">{insight.category}</span>
                            <span className={cn(
                              "text-sm font-medium",
                              insight.trend === 'up' ? 'text-green-600' : 
                              insight.trend === 'down' ? 'text-red-600' : 
                              'text-gray-600'
                            )}>
                              {insight.score}%
                              {insight.trend === 'up' && ' ↑'}
                              {insight.trend === 'down' && ' ↓'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">{insight.insight}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                
                {showInsights && progressInsights.length === 0 && (
                  <p className="text-sm text-gray-500">Complete more questions to see insights</p>
                )}
              </div>
              
              {/* Quick Tips */}
              <div className="mt-6 bg-yellow-50 rounded-lg border border-yellow-200 p-4">
                <div className="flex items-start gap-3">
                  <MessageSquare className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-900 text-sm mb-1">Tip</h4>
                    <p className="text-xs text-yellow-800">
                      Take your time with each question. Your honest responses help us provide better insights.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ResponsiveContainer>
    </div>
  );
};

export default EnhancedSurveyModule;